import { FC } from "react";
import {
  getAllocationStatus,
  getShareSummaryTableData,
  IncorporationType,
  IssuedByModel,
  ShareIssueModel,
  ShareTypeIssueModel,
  SubscriberModel,
  SummaryTableRow,
} from "./issueTransactionDomain";
import { observer } from "mobx-react";
import { TMessage } from "@/i18n/tt";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { compact } from "lodash";
import { Checkbox } from "@/components-v2/Checkbox";
import { Box, VBox } from "@/components-v2/Box";
import { IssuedBy } from "@/types/const/Transactions";
import {
  ShareBonusIssueModel,
  SubscriberForBonusIssueModel,
} from "@/dbot/capitalIncreaseThroughBonusIssue/capitalIncreaseThroughBonusIssueDomain";
import { MTextInput } from "@/components-v2/TextInput";
import { Button } from "@/components-v2/Button";
import { isMobileView } from "@/utils/screen";
import { MDateInput } from "@/components-v2/DateInput";
import { Alert } from "@/components-v2/Alert";
import { useLanguage, useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { formatAmountNumber } from "@/utils/currency";
import { BooleanFieldModel, RadioFieldModel } from "@/common/commonForms";
import { findShareTypeName } from "@/common/share";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";

const ShareTypeIssueRow: FC<{
  model: ShareTypeIssueModel;
  isBonusIssue?: boolean;
}> = observer(({ model, isBonusIssue }) => {
  const { t, lang } = useTranslation();
  return (
    <VBox data-testid={`shareClass-${model.shareType.name}`}>
      <h5>{findShareTypeName(model.shareType, lang)}</h5>
      <Box vertical={isMobileView()}>
        <MTextInput
          name={`shareClasses.${model.shareType.shareTypeCode}.number`}
          label={t("transactions.issueTransaction.number-of-new-shares")}
          model={model.numberOfShares}
          onChange={(e) => {
            model.numberOfShares.setInputValue(e);
            model.isPriceRequired(isBonusIssue);
          }}
        />
        {!isBonusIssue && (
          <MTextInput
            name={`shareClasses.${model.shareType.shareTypeCode}.price`}
            label={t("transactions.issueTransaction.share-price")}
            model={model.price}
            prefix={model.currency}
          />
        )}
      </Box>
    </VBox>
  );
});

export const ShareIssueList: FC<{
  model: ShareIssueModel;
  isBonusIssue?: BooleanFieldModel;
  onAddShareClass: () => void;
}> = observer(({ model, isBonusIssue, onAddShareClass }) => {
  const { t } = useTranslation();
  return (
    <>
      {model.shareTypeIssues.map((shareTypeIssue) => (
        <ShareTypeIssueRow
          key={shareTypeIssue.shareType.sharetypeId}
          model={shareTypeIssue}
          isBonusIssue={isBonusIssue?.value}
        />
      ))}
      <div>
        <Button
          icon={"plus"}
          label={t("add.shareclass")}
          variant={"secondary"}
          onClick={onAddShareClass}
        />
      </div>
    </>
  );
});

export const ShareSummary: FC<{
  model: ShareIssueModel;
}> = observer(({ model }) => {
  const { t, lang } = useTranslation();
  const { formatNumber } = useLocaleFormat();
  const locale = useLanguage();
  const tableColumns: Array<TableColumn<SummaryTableRow>> = [
    {
      name: "name",
      label: "",
      align: "left",
      getValue: (s) => t(s.name),
    },
    {
      name: "sharesBefore",
      label: t("transactions.issueTransaction.shares-before"),
      notSorted: true,
      getValue: (s) => formatNumber(s.sharesBefore),
    },
    {
      name: "sharesAfter",
      label: t("transactions.issueTransaction.shares-after"),
      notSorted: true,
      getValue: (s) => formatNumber(s.sharesAfter),
    },
    {
      name: "price",
      label: t("transactions.issueTransaction.nominal-amount"),
      notSorted: true,
      getValue: (s) => formatNumber(s.price),
    },
    {
      name: "shareCapitalBefore",
      label: t("transactions.issueTransaction.shareCapitalBefore"),
      notSorted: true,
      getValue: (s) => formatAmountNumber(s.shareCapitalBefore, locale),
    },
    {
      name: "newShareCapital",
      label: t("transactions.issueTransaction.new-share-capital"),
      notSorted: true,
      getValue: (s) => formatAmountNumber(s.newShareCapital, locale),
    },
  ];
  return (
    <DataTable
      variant="IN_CARD_DENSE"
      tableColumns={tableColumns}
      tableData={getShareSummaryTableData(model, lang)}
      rowProps={(row) => ({
        className: row.noChange ? "subtle" : undefined,
      })}
      getId={(row) => row.id}
    />
  );
});

export const AllocationStatus: FC<{
  model: ShareIssueModel | ShareBonusIssueModel;
}> = observer(({ model }) => {
  const { t, lang } = useTranslation();
  const { formatNumber } = useLocaleFormat();
  const allocationStatuses = getAllocationStatus(model.shareTypeIssues, model.subscribers);
  if (!allocationStatuses.length) {
    return null;
  }
  return (
    <Box className="sticky top-0 z-1" bg={"alt2"} textSize={"xs"} padding={"sm"}>
      <strong>{t("transactions.issueTransaction.remaining")}</strong>
      {allocationStatuses.map(({ shareType, total, remaining }) => (
        <div key={shareType.sharetypeId}>
          <strong>{findShareTypeName(shareType, lang)}</strong>
          <div>
            {formatNumber(remaining)} / {formatNumber(total)}
          </div>
        </div>
      ))}
    </Box>
  );
});

export const Subscribers: FC<{
  model: ShareIssueModel | ShareBonusIssueModel;
  isBonusIssue?: BooleanFieldModel;
  incorporationType?: RadioFieldModel<IncorporationType>;
}> = observer(({ model, isBonusIssue, incorporationType }) => {
  const { t, lang } = useTranslation();
  const allocationErrorMessages =
    model instanceof ShareIssueModel ? model.allocationErrorMessages : [];
  const shareTypes = model.activeShareTypeIds;
  const isDemerger = incorporationType?.value === IncorporationType.demerger;
  const isDeviatingPrice =
    model instanceof ShareIssueModel &&
    ((model.isDeviatingPrice.value && !isBonusIssue?.value) || isDemerger);
  const columns: TableColumn<SubscriberModel | SubscriberForBonusIssueModel>[] = compact([
    {
      name: "shareholder.name",
      label: t("transactions.issueTransaction.subscriber"),
      align: "left",
      getValue: (subscriber) => (
        <div className={"whitespace-nowrap"}>
          <span>{subscriber.shareholder.name}</span>
          {subscriber.shareholder.birthDateOrOrganisationNumber && (
            <span className={"subtle"}>
              {" "}
              {subscriber.shareholder.birthDateOrOrganisationNumber}
            </span>
          )}
        </div>
      ),
    },
    {
      name: "shareType",
      label: t("transactions.issueTransaction.share-class"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) => (
        <div>{findShareTypeName(subscriber.shareType, lang, { plain: true })}</div>
      ),
    },
    {
      name: "numberOfShares",
      label: t("transactions.issueTransaction.number-of-shares"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) => (
        <MTextInput
          name={`subscriber.${subscriber.shareholder.name}.${subscriber.shareType.shareTypeCode}.number`}
          model={subscriber.numberOfShares}
        />
      ),
    },
    isDeviatingPrice && {
      name: "price",
      label: t("transactions.issueTransaction.price-per-share"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) =>
        subscriber instanceof SubscriberModel ? (
          <MTextInput
            name={`subscriber.${subscriber.shareholder.name}.${subscriber.shareType.shareTypeCode}.price`}
            model={subscriber.price}
          />
        ) : null,
    },
    isDemerger && {
      name: "sharePremium",
      label: t("merger.share-allocation.share-premium"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) =>
        subscriber instanceof SubscriberModel ? (
          <MTextInput model={subscriber.sharePremium} />
        ) : null,
    },
    isDemerger && {
      name: "paidInShareCapital",
      label: t("merger.share-allocation.paid-in-share-capital"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) =>
        subscriber instanceof SubscriberModel ? (
          <MTextInput model={subscriber.paidInShareCapital} />
        ) : null,
    },
    isDemerger && {
      name: "bonusShareCapital",
      label: t("merger.share-allocation.bonus-share-capital"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) =>
        subscriber instanceof SubscriberModel ? (
          <MTextInput model={subscriber.bonusShareCapital} />
        ) : null,
    },
  ]);
  if (!shareTypes.length) {
    return null;
  }

  return (
    <>
      {allocationErrorMessages.map((error) => (
        <Alert key={t(error)} message={t(error)} type={"error"} />
      ))}
      <DataTable
        variant={"EDITABLE"}
        tableColumns={columns}
        tableData={model.subscribers}
        rowFilter={(s) => shareTypes.includes(s.shareType.sharetypeId)}
        context={{
          shouldSkipCell: (row, col) => col === 0 && row % shareTypes.length > 0,
          getRowSpan: (row, col) => {
            if (col === 0 && row % shareTypes.length === 0) {
              return shareTypes.length;
            }
          },
        }}
        getId={(row) => row.id}
      />
    </>
  );
});

export const DeviatingPriceCheckbox: FC<{
  model: BooleanFieldModel;
  isBonusIssue?: BooleanFieldModel;
}> = observer(({ model, isBonusIssue }) => {
  const { t } = useTranslation();
  if (isBonusIssue?.value) {
    return null;
  }
  return (
    <Checkbox
      label={t("transactions.issueTransaction.deviatingPrice")}
      checked={model.value ?? false}
      onChange={(event) => model.setValue(event.target.checked)}
    />
  );
});

export const IssuedByView: FC<{ model: IssuedByModel; disabled?: boolean }> = observer(
  ({ model, disabled }) => {
    const { t } = useTranslation();
    const dateLabel: TMessage =
      model.issuedBy.value === IssuedBy.BOARD_MEETING
        ? { key: "transactions.issueTransaction.boardMeetingPoADate" }
        : { key: "transactions.issueTransaction.generalMeetingDate" };

    return (
      <VBox>
        {t("transactions.issueTransaction.issuedBy")}
        <MRadioButtonGroup disabled={disabled} name={"issuedBy"} model={model.issuedBy} />
        <MDateInput
          label={t(dateLabel)}
          model={model.decisionDate}
          inline={true}
          disabled={disabled}
        />
      </VBox>
    );
  },
);
